// src/lib/api_superadmin.ts
// Cliente tipado para /api/v1/admin/superadmin/** (god-mode cross-org).
// Backend: apps/superadmin/routers/orgs.py — auth thot_admin_auth (JWT
// con is_superuser=true). Server-side enforca, este client solo asume
// que ya tienes JWT válido.

import { ensureFreshToken } from '@/lib/tokenRefresh';

const DJANGO_PROXY = '/django-api';

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

async function authHeaders(): Promise<Record<string, string>> {
  return { Authorization: `Bearer ${await getJwt()}` };
}

export class SuperadminApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'SuperadminApiError';
    this.status = status;
  }
}

async function djangoFetch<T = any>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = await authHeaders();
  if (init?.body !== undefined && !(init?.headers && (init.headers as Record<string, string>)['Content-Type'])) {
    headers['Content-Type'] = 'application/json';
  }
  const merged = { ...(init?.headers as any), ...headers };
  const res = await fetch(`${DJANGO_PROXY}${path}`, { ...init, headers: merged });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body?.detail || JSON.stringify(body);
    } catch {
      /* noop */
    }
    if (res.status === 403) {
      throw new SuperadminApiError('No tienes permisos thot_admin para esta acción.', 403);
    }
    throw new SuperadminApiError(detail, res.status);
  }
  return res.json();
}

// ── Types (matches apps/superadmin/schemas.py) ───────────────────

export interface OrgSummary {
  org_id: string;
  name: string;
  archetype: string | null;
  user_count: number;
  last_activity_at: string | null;
  primary_color: string | null;
  logo: string | null;
}

export interface UserSummary {
  id: number;
  username: string;
  email: string | null;
  is_active: boolean;
  is_superuser: boolean;
  last_login: string | null;
  date_joined: string | null;
}

export interface CreateUserPayload {
  username: string;
  email?: string;
  password: string;
  is_superuser?: boolean;
  first_name?: string;
  last_name?: string;
}

export interface CreateUserResponse {
  id: number;
  username: string;
  email: string | null;
  org_id: string;
}

export interface ImpersonateResponse {
  impersonation_token: string;
  org_id: string;
  expires_at: string;
  issued_to_username: string;
}

// ── API surface ──────────────────────────────────────────────────

export const superadminApi = {
  /** Lista todas las orgs cross-org (solo thot_admin). */
  listOrgs(): Promise<OrgSummary[]> {
    return djangoFetch<OrgSummary[]>('/api/v1/admin/superadmin/orgs');
  },

  /** Lista usuarios de una org específica. */
  listUsers(orgId: string): Promise<UserSummary[]> {
    return djangoFetch<UserSummary[]>(`/api/v1/admin/superadmin/orgs/${encodeURIComponent(orgId)}/users`);
  },

  /** Crea un usuario nuevo en una org. */
  createUser(orgId: string, payload: CreateUserPayload): Promise<CreateUserResponse> {
    return djangoFetch<CreateUserResponse>(
      `/api/v1/admin/superadmin/orgs/${encodeURIComponent(orgId)}/users`,
      { method: 'POST', body: JSON.stringify(payload) },
    );
  },

  /** Genera un impersonation token para actuar como una org. TTL en minutos. */
  impersonate(orgId: string, ttlMinutes = 30): Promise<ImpersonateResponse> {
    return djangoFetch<ImpersonateResponse>(
      `/api/v1/admin/superadmin/orgs/${encodeURIComponent(orgId)}/impersonate`,
      { method: 'POST', body: JSON.stringify({ org_id: orgId, ttl_minutes: ttlMinutes }) },
    );
  },

  /** Lista módulos habilitados para una org. */
  listOrgModules(orgId: string): Promise<any> {
    return djangoFetch(`/api/v1/admin/superadmin/orgs/${encodeURIComponent(orgId)}/modules`);
  },

  /** Lista MCP tools registradas para una org. */
  listOrgTools(orgId: string): Promise<any> {
    return djangoFetch(`/api/v1/admin/superadmin/orgs/${encodeURIComponent(orgId)}/tools`);
  },
};

// ── Impersonation helper (gestiona localStorage + reload) ────────

export const IMPERSONATION_KEY = 'impersonation_token';
export const IMPERSONATION_ORG_KEY = 'impersonation_org_id';
export const IMPERSONATION_EXPIRES_KEY = 'impersonation_expires_at';

/** Guarda el token de impersonation y recarga la app para que tome el nuevo contexto. */
export function startImpersonation(token: string, orgId: string, expiresAt: string): void {
  localStorage.setItem(IMPERSONATION_KEY, token);
  localStorage.setItem(IMPERSONATION_ORG_KEY, orgId);
  localStorage.setItem(IMPERSONATION_EXPIRES_KEY, expiresAt);
  window.location.reload();
}

/** Sale del modo impersonation (clears storage + reload). */
export function stopImpersonation(): void {
  localStorage.removeItem(IMPERSONATION_KEY);
  localStorage.removeItem(IMPERSONATION_ORG_KEY);
  localStorage.removeItem(IMPERSONATION_EXPIRES_KEY);
  window.location.reload();
}

/** Devuelve el org_id de la impersonation activa o null. */
export function getActiveImpersonation(): { token: string; orgId: string; expiresAt: string } | null {
  const token = localStorage.getItem(IMPERSONATION_KEY);
  const orgId = localStorage.getItem(IMPERSONATION_ORG_KEY);
  const expiresAt = localStorage.getItem(IMPERSONATION_EXPIRES_KEY);
  if (!token || !orgId || !expiresAt) return null;
  if (new Date(expiresAt).getTime() < Date.now()) {
    stopImpersonation();
    return null;
  }
  return { token, orgId, expiresAt };
}

// ── JWT helper para detectar is_superuser client-side ────────────
// Decode SOLO del payload (sin verificar firma — el server enforca).
// Useful para gating de módulos como multi-org (god-mode).

interface JwtPayload {
  sub?: string;
  exp?: number;
  is_superuser?: boolean;
  org_id?: string;
  scopes?: string[];
  [key: string]: unknown;
}

export function decodeJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // base64url → base64 padding fix
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - b64.length % 4) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/** True si el JWT actual carga is_superuser=true (para gating de god-mode). */
export function isSuperuserFromToken(): boolean {
  const token = localStorage.getItem('auth_token');
  if (!token) return false;
  const payload = decodeJwt(token);
  return !!(payload && payload.is_superuser === true);
}

/** `org_id` del JWT actual (para gating per-org del switcher, p.ej. hiddenForOrgs). */
export function orgIdFromToken(): string {
  const token = localStorage.getItem('auth_token');
  if (!token) return '';
  const payload = decodeJwt(token);
  return (payload && typeof payload.org_id === 'string') ? payload.org_id : '';
}
